/* ============================================================
   ALMA SHAPE — CART DRAWER
   Live Shopify total sync
   ============================================================ */

(() => {
  const AlmaCartTotals = {
    timer: null,
    controller: null,
    generation: 0,

    getRoot() {
      return window.Shopify?.routes?.root || '/';
    },

    formatMoney(amountInCents, currency = 'CHF') {
      const locale = document.documentElement.lang || 'fr-CH';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'code',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
          .format((Number(amountInCents) || 0) / 100)
          .replace(/\u00a0/g, ' ');
      } catch (error) {
        return `CHF ${((Number(amountInCents) || 0) / 100).toFixed(2)}`;
      }
    },

    updateNodeText(node, value) {
      if (!node) return;

      if (node.textContent.trim() !== value.trim()) {
        node.textContent = value;
      }
    },

    apply(cart) {
      if (!cart) return;

      const currency =
        cart.currency ||
        window.Shopify?.currency?.active ||
        'CHF';

      /*
       * cart.total_price = true Shopify cart total after discounts.
       * This is the amount we want displayed immediately before checkout.
       */
      const total = Number(cart.total_price) || 0;
      const formatted = this.formatMoney(total, currency);

      document
        .querySelectorAll('[data-alma-cart-total]')
        .forEach((node) => {
          this.updateNodeText(node, formatted);
        });

      document
        .querySelectorAll('[data-alma-cart-checkout-total]')
        .forEach((node) => {
          this.updateNodeText(node, ``);
        });

      /*
       * Optional generic fallback:
       * if another total element exists in the custom cart drawer,
       * these selectors will also be synchronized.
       */
      document
        .querySelectorAll(
          '.alma-cart-total-value, ' +
          '[data-cart-total-value], ' +
          '[data-cart-drawer-total]'
        )
        .forEach((node) => {
          this.updateNodeText(node, formatted);
        });

      return cart;
    },

    async fetchCart(generation) {
      if (generation !== this.generation) return null;

      if (this.controller) {
        this.controller.abort();
      }

      this.controller = new AbortController();

      const root = this.getRoot();
      const url = `${root}cart.js?alma=${Date.now()}-${Math.random()}`;

      try {
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          signal: this.controller.signal,
          headers: {
            Accept: 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache'
          }
        });

        if (!response.ok) {
          throw new Error(`Cart request failed: ${response.status}`);
        }

        const cart = await response.json();

        if (generation !== this.generation) {
          return null;
        }

        this.apply(cart);

        return cart;
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('Alma cart total sync failed:', error);
        }

        return null;
      }
    },

    async run(generation) {
      const delays = [0];

      for (const delay of delays) {
        if (generation !== this.generation) return;

        if (delay > 0) {
          await new Promise((resolve) => {
            window.setTimeout(resolve, delay);
          });
        }

        if (generation !== this.generation) return;

        await this.fetchCart(generation);
      }
    },

    schedule(delay = 60) {
      this.generation += 1;

      const generation = this.generation;

      if (this.timer) {
        window.clearTimeout(this.timer);
      }

      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }

      this.timer = window.setTimeout(() => {
        this.run(generation);
      }, delay);
    },

    immediate() {
      this.schedule(0);
    }
  };

  window.AlmaCartTotals = AlmaCartTotals;

  window.almaSyncCartDrawerTotals = function almaSyncCartDrawerTotals(delay = 60) {
    AlmaCartTotals.schedule(delay);
  };
})();


class CartDrawer extends HTMLElement {
  constructor() {
    super();

    /*
     * Timer interval currently running.
     */
    this.almaUrgencyInterval = null;

    /*
     * Absolute timestamp at which the current countdown ends.
     *
     * IMPORTANT:
     * This value belongs to the <cart-drawer> custom element itself,
     * so rebuilding the INNER HTML of the drawer does not reset it.
     *
     * This prevents quantity changes / upsells / Shopify section
     * rerenders from restarting the countdown.
     */
    this.almaUrgencyEndAt = null;

    /*
     * Used to detect if the merchant changes the timer duration
     * in the Theme Editor.
     */
    this.almaUrgencyDurationSeconds = null;

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.addEventListener(
      'click',
      this.handleAlmaUpsellClick.bind(this)
    );

    this.addEventListener(
      'change',
      this.handleAlmaUpsellChange.bind(this)
    );

    this.setHeaderCartIconAccessibility();
  }


  /* ============================================================
     ALMA UPSELL — VARIANT CHANGE
     ============================================================ */

  handleAlmaUpsellChange(event) {
    const select = event.target.closest(
      'select[data-alma-upsell-option]'
    );

    if (!select || !this.contains(select)) return;

    const card = select.closest(
      '[data-alma-cart-upsell]'
    );

    if (!card) return;

    const variantInput = card.querySelector(
      '[data-alma-upsell-variant]'
    );

    const price = card.querySelector(
      '[data-alma-upsell-price]'
    );

    const button = card.querySelector(
      '[data-alma-upsell-add]'
    );

    const status = card.querySelector(
      '[data-alma-upsell-status]'
    );

    const variantsScript = card.querySelector(
      '[data-alma-upsell-variants]'
    );

    const optionSelects = [
      ...card.querySelectorAll(
        'select[data-alma-upsell-option]'
      )
    ];

    let variants = [];

    try {
      variants = JSON.parse(
        variantsScript?.textContent || '[]'
      );
    } catch (error) {
      variants = [];
    }

    const selectedOptions = optionSelects.map(
      (optionSelect) => optionSelect.value
    );

    const matchedVariant = variants.find(
      (variant) => {
        if (
          !variant?.available ||
          !Array.isArray(variant.options)
        ) {
          return false;
        }

        return variant.options.every(
          (value, index) =>
            String(value) ===
            String(selectedOptions[index])
        );
      }
    );

    if (matchedVariant) {
      if (variantInput) {
        variantInput.value = matchedVariant.id;
      }

      if (price && matchedVariant.price) {
        price.textContent = matchedVariant.price;
      }

      if (button) {
        button.disabled = false;
      }

      if (status) {
        status.textContent = '';
      }
    } else {
      if (variantInput) {
        variantInput.value = '';
      }

      if (button) {
        button.disabled = true;
      }

      if (status) {
        status.textContent =
          'Cette combinaison est momentanément indisponible.';
      }
    }
  }


  /* ============================================================
     ALMA UPSELL — ADD TO CART
     ============================================================ */

  async handleAlmaUpsellClick(event) {
    const button = event.target.closest(
      '[data-alma-upsell-add]'
    );

    if (!button || !this.contains(button)) return;

    event.preventDefault();

    const card = button.closest(
      '[data-alma-cart-upsell]'
    );

    const variantField = card?.querySelector(
      '[data-alma-upsell-variant]'
    );

    const status = card?.querySelector(
      '[data-alma-upsell-status]'
    );

    const label = button.querySelector(
      '[data-alma-upsell-add-label]'
    );

    const variantId = variantField?.value;

    if (!variantId) {
      if (status) {
        status.textContent =
          'Choisissez une option disponible.';
      }

      return;
    }

    const originalLabel =
      label?.textContent || 'AJOUTER +';

    button.disabled = true;

    if (label) {
      label.textContent = 'AJOUT...';
    }

    if (status) {
      status.textContent = '';
    }

    try {
      const sections = this
        .getSectionsToRender()
        .map((section) => section.id);

      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1
              }
            ],
            sections,
            sections_url: window.location.pathname
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.description ||
          'Impossible d’ajouter ce produit.'
        );
      }

      this.renderContents(result);

      /*
       * Force server-side cart total verification only after
       * Shopify has accepted the add-to-cart operation.
       */
      window.almaSyncCartDrawerTotals?.(80);
    } catch (error) {
      button.disabled = false;

      if (label) {
        label.textContent = originalLabel;
      }

      if (status) {
        status.textContent =
          error.message ||
          'Une erreur est survenue.';
      }
    }
  }


  /* ============================================================
     HEADER CART ACCESSIBILITY
     ============================================================ */

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.open(cartLink);
      }
    );

    cartLink.addEventListener(
      'keydown',
      (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      }
    );
  }


  /* ============================================================
     OPEN DRAWER
     ============================================================ */

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    window.setTimeout(() => {
      this.classList.add(
        'animate',
        'active'
      );
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : document.getElementById(
                'CartDrawer'
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        if (
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      {
        once: true
      }
    );


    /* ============================================================
       ALMA CART URGENCY — VISUAL ONLY
       Never modifies or clears the Shopify cart.
       ============================================================ */

    this.startAlmaUrgencyTimer();


    document.body.classList.add(
      'overflow-hidden'
    );

    /*
     * Drawer is now visible.
     * Ask Shopify for the current cart total.
     */
    window.almaSyncCartDrawerTotals?.(
      30
    );
  }


  /* ============================================================
     ALMA CART URGENCY TIMER
     ============================================================ */

  startAlmaUrgencyTimer() {
    const timerBar = this.querySelector(
      '.alma-cart-urgency'
    );

    const clock = timerBar?.querySelector(
      '.alma-cart-urgency-clock'
    );

    if (!timerBar || !clock) return;


    /*
     * Stop only the visible interval.
     *
     * IMPORTANT:
     * We DO NOT reset almaUrgencyEndAt here.
     *
     * Shopify can rebuild the cart drawer after:
     * - quantity change
     * - item deletion
     * - upsell addition
     * - cart section refresh
     *
     * The existing deadline must survive all of those actions.
     */
    if (this.almaUrgencyInterval) {
      window.clearInterval(
        this.almaUrgencyInterval
      );

      this.almaUrgencyInterval = null;
    }


    /*
     * Duration configured from Theme Editor.
     */
    const configuredMinutes =
      Number.parseInt(
        timerBar.dataset.durationMinutes,
        10
      );

    const durationMinutes =
      Number.isFinite(configuredMinutes) &&
      configuredMinutes > 0
        ? configuredMinutes
        : 10;

    const durationSeconds =
      durationMinutes * 60;


    /*
     * If the merchant changes the duration in Theme Editor,
     * reset the countdown using the new value.
     *
     * Otherwise preserve the current timer.
     */
    if (
      this.almaUrgencyDurationSeconds !== null &&
      this.almaUrgencyDurationSeconds !== durationSeconds
    ) {
      this.almaUrgencyEndAt = null;
    }

    this.almaUrgencyDurationSeconds =
      durationSeconds;


    /*
     * Create the deadline only the FIRST time.
     *
     * Changing quantity / adding products / rerendering
     * does not change this timestamp.
     */
    if (!this.almaUrgencyEndAt) {
      this.almaUrgencyEndAt =
        Date.now() +
        durationSeconds * 1000;
    }


    const render = () => {
      const now = Date.now();

      let remainingSeconds =
        Math.ceil(
          (
            this.almaUrgencyEndAt -
            now
          ) / 1000
        );


      /*
       * Purely visual scarcity timer.
       *
       * Once 00:00 is reached it starts another cycle.
       *
       * It NEVER:
       * - clears the cart
       * - reloads the page
       * - removes products
       * - disables checkout
       */
      if (remainingSeconds < 0) {
        this.almaUrgencyEndAt =
          now +
          durationSeconds * 1000;

        remainingSeconds =
          durationSeconds;
      }


      const minutes =
        Math.floor(
          remainingSeconds / 60
        );

      const seconds =
        remainingSeconds % 60;

      const value =
        `${String(minutes).padStart(2, '0')}:` +
        `${String(seconds).padStart(2, '0')}`;


      /*
       * Shopify can theoretically render multiple copies
       * during section replacement, so update every timer
       * clock currently present inside the cart drawer.
       */
      this
        .querySelectorAll(
          '.alma-cart-urgency-clock'
        )
        .forEach((node) => {
          node.hidden = false;

          if (
            node.textContent !== value
          ) {
            node.textContent = value;
          }
        });
    };


    /*
     * Render immediately so the customer never sees
     * an old / empty value after Shopify rebuilds the drawer.
     */
    render();


    /*
     * Only the visual refresh interval is recreated.
     *
     * The countdown itself is based on almaUrgencyEndAt,
     * therefore restarting this interval DOES NOT restart
     * the countdown.
     */
    this.almaUrgencyInterval =
      window.setInterval(
        render,
        1000
      );
  }


  /* ============================================================
     CLOSE DRAWER
     ============================================================ */

  close() {
    /*
     * Stop rendering while drawer is closed to avoid running
     * unnecessary intervals.
     *
     * We intentionally KEEP almaUrgencyEndAt.
     *
     * Therefore:
     * 09:20 -> close drawer
     * wait 15 seconds
     * reopen -> approximately 09:05
     *
     * The timer does NOT restart.
     */
    if (this.almaUrgencyInterval) {
      window.clearInterval(
        this.almaUrgencyInterval
      );

      this.almaUrgencyInterval = null;
    }

    this.classList.remove(
      'active'
    );

    removeTrapFocus(
      this.activeElement
    );

    document.body.classList.remove(
      'overflow-hidden'
    );
  }


  /* ============================================================
     SUMMARY ACCESSIBILITY
     ============================================================ */

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote
        .nextElementSibling
        .getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote
          .nextElementSibling
          .id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );
      }
    );

    cartDrawerNote
      .parentElement
      .addEventListener(
        'keyup',
        onKeyUpEscape
      );
  }


  /* ============================================================
     RENDER CART CONTENTS
     ============================================================ */

  renderContents(parsedState) {
    /*
     * When first adding an item to an empty cart,
     * Dawn keeps is-empty on the custom element.
     */
    this.classList.remove(
      'is-empty'
    );

    this
      .querySelector(
        'cart-drawer-items'
      )
      ?.classList.remove(
        'is-empty'
      );

    this.productId =
      parsedState.id;


    this
      .getSectionsToRender()
      .forEach((section) => {
        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        if (!sectionElement) {
          return;
        }

        const sectionHTML =
          parsedState?.sections?.[
            section.id
          ];

        if (!sectionHTML) {
          return;
        }

        sectionElement.innerHTML =
          this.getSectionInnerHTML(
            sectionHTML,
            section.selector
          );
      });


    /*
     * Rendering is complete.
     * The old total must NEVER be trusted at this point.
     *
     * We deliberately wait until after the new drawer HTML exists
     * and then fetch the canonical Shopify cart.
     */
    window.almaSyncCartDrawerTotals?.(
      80
    );


    window.setTimeout(() => {
      const overlay =
        this.querySelector(
          '#CartDrawer-Overlay'
        );

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }

      /*
       * Reopening after Shopify rerender WILL NOT restart
       * the urgency timer because almaUrgencyEndAt survives.
       */
      this.open();

      window.almaSyncCartDrawerTotals?.(
        80
      );
    }, 20);
  }


  /* ============================================================
     SECTION HTML
     ============================================================ */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return '';

    const parsed =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const target =
      parsed.querySelector(
        selector
      );

    return target
      ? target.innerHTML
      : '';
  }


  /* ============================================================
     SECTIONS TO RENDER
     ============================================================ */

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }


  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      );
  }


  setActiveElement(element) {
    this.activeElement =
      element;
  }
}


customElements.define(
  'cart-drawer',
  CartDrawer
);


/* ============================================================
   CART DRAWER ITEMS
   ============================================================ */

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}


customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);